import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Edit } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SubmissionPhotoField } from "@/components/SubmissionPhotoField";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { key: "name", label: "Name", type: "text" },
  { key: "city", label: "City", type: "text" },
  { key: "state", label: "State (full)", type: "text" },
  { key: "state_code", label: "State code", type: "text" },
  { key: "railroad", label: "Railroad", type: "text" },
  { key: "status", label: "Status (original / relocated)", type: "text" },
  { key: "year_built", label: "Year built", type: "number" },
  { key: "address", label: "Address", type: "text" },
  { key: "latitude", label: "Latitude", type: "number" },
  { key: "longitude", label: "Longitude", type: "number" },
];

export default function SubmitEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [depot, setDepot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [edits, setEdits] = useState({});
  const [notes, setNotes] = useState("");
  const [description, setDescription] = useState("");
  const [preservedEquipment, setPreservedEquipment] = useState("");
  const [contributorName, setContributorName] = useState("");
  const [contributorEmail, setContributorEmail] = useState("");
  const [creditPublicly, setCreditPublicly] = useState(true);
  const [photo, setPhoto] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/depots/${id}`)
      .then((r) => {
        setDepot(r.data);
        setDescription(r.data.description || "");
        setPreservedEquipment(r.data.preserved_equipment || "");
      })
      .catch(() => toast.error("Could not load depot"))
      .finally(() => setLoading(false));
  }, [id]);

  const setEdit = (key, value) =>
    setEdits((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!depot) return;

    if (!contributorName.trim() || !contributorEmail.trim() || !contributorEmail.includes("@")) {
      toast.error("Please tell us your name and email so we can credit you and follow up.");
      return;
    }

    const payload = {};
    for (const { key, type } of FIELDS) {
      if (edits[key] === undefined || edits[key] === "") continue;
      const original = depot[key];
      let val = edits[key];
      if (type === "number") {
        val = parseFloat(val);
        if (isNaN(val)) continue;
      }
      if (val !== original) payload[key] = val;
    }
    if (description !== (depot.description || "")) {
      payload.description = description;
    }
    if (preservedEquipment !== (depot.preserved_equipment || "")) {
      payload.preserved_equipment = preservedEquipment;
    }
    // Museum / visitor / active-line edits — pass through if set
    for (const k of ["museum_status", "visiting_hours", "amtrak_station_code"]) {
      const v = edits[k];
      if (v !== undefined && v !== "" && v !== (depot[k] || "")) {
        payload[k] = v;
      }
    }
    if (edits.on_active_rail_line !== undefined
        && edits.on_active_rail_line !== !!depot.on_active_rail_line) {
      payload.on_active_rail_line = !!edits.on_active_rail_line;
    }

    if (Object.keys(payload).length === 0 && !photo) {
      toast.error("Change at least one field or attach a photo before submitting.");
      return;
    }

    try {
      setSaving(true);
      await axios.post(
        `${API}/submissions`,
        {
          kind: "edit_depot",
          target_id: depot.id,
          payload: Object.keys(payload).length > 0 ? payload : { _photo_only: true },
          notes: notes.trim() || null,
          contributor_name: contributorName.trim(),
          contributor_email: contributorEmail.trim(),
          credit_publicly: creditPublicly,
          photo_data: photo?.photo_data || null,
          photo_caption: photo?.photo_caption || null,
        },
        { withCredentials: true }
      );
      toast.success(
        photo
          ? "Thanks — your correction and photo are queued for review."
          : "Thanks — your correction is queued for review."
      );
      navigate(`/depot/${depot.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-10 h-10 border-3 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!depot) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-white/70">
        Depot not found.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <header className="sticky top-0 z-50 glass border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="text-white hover:bg-white/5"
              data-testid="edit-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1 min-w-0">
              <h1 className="font-serif font-bold text-lg truncate text-white">
                Suggest a correction
              </h1>
              <p className="text-sm text-white/60 truncate">
                {depot.name} — {depot.city}, {depot.state}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <Card className="bg-[#111] border-white/5">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Edit className="w-5 h-5 text-amber-500" />
              Only fill in fields you want to change
            </CardTitle>
            <p className="text-sm text-white/60 pt-2">
              An editor will review your correction. We&apos;ll never publish your email address.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="p-4 bg-white/5 rounded-lg border border-white/5 space-y-3">
                <p className="text-[11px] text-amber-500 uppercase tracking-[0.2em] font-semibold">
                  About you
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-white/80 mb-1" htmlFor="cn">Your name *</label>
                    <Input id="cn" data-testid="edit-cname" value={contributorName} onChange={(e) => setContributorName(e.target.value)} className="bg-white/5 border-white/10 text-white" />
                  </div>
                  <div>
                    <label className="block text-sm text-white/80 mb-1" htmlFor="ce">Your email *</label>
                    <Input id="ce" type="email" data-testid="edit-cemail" value={contributorEmail} onChange={(e) => setContributorEmail(e.target.value)} className="bg-white/5 border-white/10 text-white" placeholder="never shown publicly" />
                  </div>
                </div>
                <label className="flex items-start gap-2 pt-1 cursor-pointer group" data-testid="credit-toggle">
                  <input
                    type="checkbox"
                    checked={creditPublicly}
                    onChange={(e) => setCreditPublicly(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-white/20 bg-white/5 accent-amber-500"
                  />
                  <span className="text-sm text-white/80">
                    Credit me publicly on the{" "}
                    <span className="text-amber-400">Contributors</span> page.
                  </span>
                </label>
              </div>

              {FIELDS.map(({ key, label, type }) => (
                <div key={key}>
                  <label className="block text-sm text-white/80 mb-1" htmlFor={`f-${key}`}>
                    {label}
                    <span className="text-white/40 ml-2">
                      current: {String(depot[key] ?? "—")}
                    </span>
                  </label>
                  <Input
                    id={`f-${key}`}
                    type={type}
                    step={type === "number" ? "any" : undefined}
                    value={edits[key] ?? ""}
                    onChange={(e) => setEdit(key, e.target.value)}
                    placeholder={`New ${label.toLowerCase()}`}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid={`edit-field-${key}`}
                  />
                </div>
              ))}

              <div>
                <label className="block text-sm text-white/80 mb-1" htmlFor="desc">Description</label>
                <Textarea
                  id="desc"
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="edit-field-description"
                />
              </div>

              <div>
                <label className="block text-sm text-white/80 mb-1" htmlFor="museum">
                  Museum / Site Status
                  <span className="text-white/40 text-xs ml-2 font-normal">
                    current: {String(depot.museum_status ?? "—")}
                  </span>
                </label>
                <select
                  id="museum"
                  value={edits.museum_status ?? ""}
                  onChange={(e) => setEdit("museum_status", e.target.value)}
                  className="w-full h-10 rounded-md bg-white/5 border border-white/10 text-white px-3"
                  data-testid="edit-field-museum_status"
                >
                  <option value="">(no change)</option>
                  {[
                    ["active_station", "Active passenger station"],
                    ["museum", "Museum — open to the public"],
                    ["restored_display", "Restored / on display"],
                    ["adaptive_reuse", "Adaptive reuse (restaurant, shop, etc.)"],
                    ["abandoned", "Abandoned"],
                    ["demolished", "Demolished"],
                  ].map(([v, l]) => (
                    <option key={v} value={v} className="bg-[#111]">{l}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-white/80 mb-1" htmlFor="hours">
                  Visiting hours
                  <span className="text-white/40 text-xs ml-2 font-normal">
                    e.g. "Sat-Sun 10am-4pm"
                  </span>
                </label>
                <Input
                  id="hours"
                  value={edits.visiting_hours ?? ""}
                  onChange={(e) => setEdit("visiting_hours", e.target.value)}
                  placeholder={depot.visiting_hours || "—"}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="edit-field-visiting_hours"
                />
              </div>

              <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer" data-testid="edit-field-on_active_rail_line">
                <input
                  type="checkbox"
                  checked={edits.on_active_rail_line ?? depot.on_active_rail_line ?? false}
                  onChange={(e) => setEdit("on_active_rail_line", e.target.checked)}
                  className="h-4 w-4 rounded border-white/20 bg-white/5 accent-amber-500"
                />
                Trains still pass this location on an active rail line
              </label>

              <div>
                <label className="block text-sm text-white/80 mb-1" htmlFor="amtrak-code">
                  Amtrak station code (if applicable)
                  <span className="text-white/40 text-xs ml-2 font-normal">
                    3-letter code, e.g. "CHI" for Chicago Union
                  </span>
                </label>
                <Input
                  id="amtrak-code"
                  value={edits.amtrak_station_code ?? ""}
                  onChange={(e) => setEdit("amtrak_station_code", e.target.value.toUpperCase().slice(0, 3))}
                  placeholder={depot.amtrak_station_code || "—"}
                  maxLength={3}
                  className="bg-white/5 border-white/10 text-white uppercase font-mono"
                  data-testid="edit-field-amtrak_station_code"
                />
              </div>

              <div>
                <label className="block text-sm text-white/80 mb-1" htmlFor="preserved">
                  Preserved Equipment on Site
                  <span className="text-white/40 text-xs ml-2 font-normal">
                    Locomotives, cabooses, cars, etc. — one per line
                  </span>
                </label>
                <Textarea
                  id="preserved"
                  rows={3}
                  value={preservedEquipment}
                  onChange={(e) => setPreservedEquipment(e.target.value)}
                  placeholder={"e.g.\nIllinois Central Gulf GP10 #199620\nICRR wooden caboose #9425"}
                  className="bg-white/5 border-white/10 text-white font-mono text-sm"
                  data-testid="edit-field-preserved"
                />
              </div>

              <SubmissionPhotoField value={photo} onChange={setPhoto} label="Attach a photo (optional)" />

              <div>
                <label className="block text-sm text-white/80 mb-1" htmlFor="notes">Notes for the reviewer</label>
                <Textarea
                  id="notes"
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Sources, references, why this change is correct."
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="edit-field-notes"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Link to={`/depot/${depot.id}`}>
                  <Button type="button" variant="outline" className="border-white/10 text-white hover:bg-white/5">
                    Cancel
                  </Button>
                </Link>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-amber-500 hover:bg-amber-600 text-black"
                  data-testid="edit-submit-btn"
                >
                  {saving ? "Submitting…" : "Submit correction"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
